import { TextField } from '@mui/material';

export interface NativeSelectOption {
  value: string;
  label: string;
}

export interface NativeSelectFieldProps {
  label: string;
  value: string;
  options: NativeSelectOption[];
  onChange: (value: string) => void;
  name: string;
  testId: string;
  disabled?: boolean;
  /** Renders a leading empty option, for optional filters. */
  includeBlank?: boolean;
  blankLabel?: string;
  minWidth?: number;
}

export const NATIVE_SELECT_DEFAULT_MIN_WIDTH = 220;

/**
 * A native `<select>` rendered through MUI's `TextField`.
 *
 * `HHSelect` seeds its value once (`initialSelectValue`) and has to be
 * remounted with a `key` to follow state changes — see the ingestions
 * `FilterBar`. The configuration form's dropdowns *are* controlled state
 * (changing an action's type rewrites its metadata fields underneath it),
 * so this uses a genuinely controlled select instead. Native rendering also
 * keeps the value reachable from a plain `change` event, which keeps both
 * the unit tests and assistive tech straightforward.
 */
export const NativeSelectField = ({
  label,
  value,
  options,
  onChange,
  name,
  testId,
  disabled = false,
  includeBlank = false,
  blankLabel = 'All',
  minWidth = NATIVE_SELECT_DEFAULT_MIN_WIDTH,
}: NativeSelectFieldProps): JSX.Element => {
  const hasMatchingOption = options.some(option => option.value === value);

  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      disabled={disabled}
      onChange={(event): void => onChange(event.target.value)}
      sx={{ minWidth }}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ name, 'aria-label': label, 'data-testid': testId }}
    >
      {(includeBlank || !hasMatchingOption) && (
        <option value="">{includeBlank ? blankLabel : ''}</option>
      )}
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </TextField>
  );
};
